import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { DailyReflection } from '@/types/reflection';
import type { Goal } from '@/types/goal';
import {
  parseValuesTags,
  computeTopValue,
  filterReflectionsToLastNDays,
} from '@/lib/reflection-stats';
import { reportError } from '@/lib/error-reporting';

export interface WeeklyDigest {
  daysReflected: number;
  topExpressed: { name: string; count: number } | null;
  topSuppressed: { name: string; count: number } | null;
  insight: string | null;
}

function getWeekStart(now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() - 6);
  return d.toISOString().slice(0, 10);
}

export function useWeeklyDigest(
  reflections: DailyReflection[],
  goals: Goal[]
): { digest: WeeklyDigest | null; dismissed: boolean; dismiss: () => void } {
  const { user } = useAuth();
  const userId = user?.id;
  const weekStart = getWeekStart();

  const [insight, setInsight] = useState<string | null>(null);
  const [insightLoading, setInsightLoading] = useState(false);
  // Session-local dismiss (set when user clicks X)
  const [sessionDismissed, setSessionDismissed] = useState(false);

  // Check localStorage synchronously during render to prevent single-frame flicker.
  // useEffect would fire after paint, briefly showing the card before hiding it.
  const dismissed = sessionDismissed ||
    (!!userId && localStorage.getItem(`meq_digest_dismiss_${userId}_${weekStart}`) === '1');

  // Compute stats from reflections
  const stats = useMemo(() => {
    const weekReflections = filterReflectionsToLastNDays(reflections, 7);
    const daysReflected = weekReflections.length;

    if (daysReflected < 3) return null;

    const allExpressed: string[] = [];
    const allSuppressed: string[] = [];

    for (const r of weekReflections) {
      const tags = parseValuesTags(r.valuesTags);
      allExpressed.push(...tags.expressed);
      allSuppressed.push(...tags.suppressed);
    }

    return {
      daysReflected,
      topExpressed: computeTopValue(allExpressed),
      topSuppressed: computeTopValue(allSuppressed),
    };
  }, [reflections]);

  // Fetch AI insight (cached in localStorage per week)
  useEffect(() => {
    if (!userId || !stats || dismissed) return;

    const cacheKey = `meq_digest_${userId}_${weekStart}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      setInsight(cached);
      return;
    }

    let cancelled = false;
    setInsightLoading(true);

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          'generate-reflection-digest',
          { body: {} }
        );

        if (cancelled) return;

        if (error) {
          console.warn('[useWeeklyDigest] Edge function error:', error);
          return;
        }

        const text = data?.insight || '';
        if (text) {
          localStorage.setItem(cacheKey, text);
          setInsight(text);
        }
      } catch (err) {
        reportError(err, 'reflections', { action: 'weekly-digest' });
      } finally {
        if (!cancelled) setInsightLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userId, stats, dismissed, weekStart]);

  const digest: WeeklyDigest | null = stats
    ? {
        ...stats,
        insight: insightLoading ? null : insight,
      }
    : null;

  const dismiss = () => {
    if (userId) {
      localStorage.setItem(`meq_digest_dismiss_${userId}_${weekStart}`, '1');
    }
    setSessionDismissed(true);
  };

  return { digest, dismissed, dismiss };
}
